import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, Spinner } from 'react-bootstrap';
import { useDownloadAnalysisPdfMutation } from '@/app/PageSpeed/api/analysisApi';
import type {
  AnalysisPdfSection,
  ReportLanguage
} from '@/app/PageSpeed/api/types/analysisApiTypes';
import { errorMessage, successMessage } from '@/utils/toast';

type AnalysisPdfDownloadMenuProps = {
  taskId: string;
  reportLanguage: ReportLanguage;
};

type DownloadOption = {
  label: string;
  sections: AnalysisPdfSection[] | null;
  fileName: string;
};

const DOWNLOAD_OPTIONS: DownloadOption[] = [
  { label: 'خروجی همه', sections: null, fileName: 'analysis-all.pdf' },
  {
    label: 'خروجی Performance',
    sections: ['performance'],
    fileName: 'analysis-performance.pdf'
  },
  {
    label: 'خروجی SEO',
    sections: ['seo'],
    fileName: 'analysis-seo.pdf'
  },
  {
    label: 'خروجی Accessibility',
    sections: ['accessibility'],
    fileName: 'analysis-accessibility.pdf'
  },
  {
    label: 'خروجی Best Practices',
    sections: ['best-practices'],
    fileName: 'analysis-best-practices.pdf'
  }
];

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const AnalysisPdfDownloadMenu = ({
  taskId,
  reportLanguage
}: AnalysisPdfDownloadMenuProps) => {
  const [downloadAnalysisPdf, { isLoading }] =
    useDownloadAnalysisPdfMutation();

  const handleDownload = async (option: DownloadOption) => {
    try {
      const blob = await downloadAnalysisPdf({
        task_id: taskId,
        sections: option.sections,
        report_lang: reportLanguage
      }).unwrap();

      if (!(blob instanceof Blob) || blob.size === 0) {
        errorMessage('فایل PDF خالی برگشت. لطفاً دوباره تلاش کنید.');
        return;
      }

      if (blob.type.includes('json')) {
        errorMessage('ساخت فایل PDF ناموفق بود.');
        return;
      }

      triggerBlobDownload(blob, option.fileName);
      successMessage('فایل PDF آماده شد.');
    } catch {
      errorMessage('در ساخت یا دانلود فایل PDF خطایی رخ داد.');
    }
  };

  return (
    <Dropdown align="end" className="me-auto">
      <Dropdown.Toggle
        size="sm"
        variant="outline-primary"
        disabled={isLoading}
        className="d-inline-flex align-items-center gap-2"
        style={{ width: '140px', maxWidth: '100%', height: '31px', direction: 'rtl' }}
      >
        {isLoading ? (
          <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
        ) : (
          <FontAwesomeIcon icon="file-pdf" />
        )}
        <span>خروجی</span>
        <span>PDF</span>
      </Dropdown.Toggle>
      <Dropdown.Menu className="border py-0 text-end" dir="rtl">
        {DOWNLOAD_OPTIONS.map(option => (
          <Dropdown.Item
            key={option.label}
            className="link-600 fs--1 py-2 text-end"
            onClick={() => void handleDownload(option)}
          >
            {option.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default AnalysisPdfDownloadMenu;
